import { useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { InfoHint } from "@/components/ui/info-hint";
import { KpiBasisLabel, KpiBasisNote } from "@/components/reports/KpiBasisLabel";
import { useTripsTraveledKpis } from "@/hooks/useInvoices";
import { useGoals, useUpsertPersonalGoal, useUpsertTeamGoal } from "@/hooks/useGoals";
import { GoalEditDialog } from "@/components/goals/GoalEditDialog";
import { SectionLoadError } from "@/components/invoicing/SectionErrorBoundary";
import { useQueryClient } from "@tanstack/react-query";

interface TripForecastResult {
  onTrack: boolean;
  label: string;
}

function computeTripForecast(
  tripsTraveled: number,
  goalTrips: number,
): TripForecastResult {
  if (goalTrips <= 0) {
    return { onTrack: true, label: "" };
  }

  const now = new Date();
  const yearStart = new Date(now.getFullYear(), 0, 1);
  const yearEnd = new Date(now.getFullYear() + 1, 0, 1);
  const totalMonths = 12;
  const elapsedMs = now.getTime() - yearStart.getTime();
  const totalMs = yearEnd.getTime() - yearStart.getTime();
  const remainingMonths = Math.max(1, totalMonths * (1 - elapsedMs / totalMs));

  const remaining = goalTrips - tripsTraveled;
  if (remaining <= 0) {
    return { onTrack: true, label: "Goal reached!" };
  }

  const perMonth = Math.ceil(remaining / remainingMonths);
  const projected = tripsTraveled / (elapsedMs / totalMs);
  const onTrack = projected >= goalTrips;

  const label = `You need ~${perMonth} trip${perMonth !== 1 ? "s" : ""} per month to hit your goal`;
  return { onTrack, label };
}

interface TripsTraveledCardProps {
  tramsAccountId?: number;
  viewMode: "my" | "all";
  returnDateFrom?: string;
  returnDateTo?: string;
}

export function TripsTraveledCard({
  tramsAccountId,
  viewMode,
  returnDateFrom,
  returnDateTo,
}: TripsTraveledCardProps) {
  const queryClient = useQueryClient();
  const currentYear = new Date().getFullYear();

  const {
    data: kpiData,
    isLoading: kpiLoading,
    error: kpiError,
  } = useTripsTraveledKpis({ tramsAccountId, viewMode, returnDateFrom, returnDateTo });

  const {
    data: goalsData,
    isLoading: goalsLoading,
    error: goalsError,
  } = useGoals(currentYear);

  const upsertPersonal = useUpsertPersonalGoal();
  const upsertTeam = useUpsertTeamGoal();

  const isLoading = kpiLoading || goalsLoading;
  const error = kpiError || goalsError;

  const canEditTeamGoals = goalsData?.canEditTeamGoals ?? false;
  const teamAvailable =
    canEditTeamGoals ||
    Boolean(goalsData?.team) ||
    (goalsData?.showTeamByDefault ?? false);

  const goalScope: "team" | "personal" =
    viewMode === "all" && teamAvailable ? "team" : "personal";

  const activeGoal = goalScope === "team" ? goalsData?.team : goalsData?.personal;
  const goalTrips = activeGoal?.tripsTraveledGoal ?? 0;
  const tripsTraveled = kpiData?.tripsTraveledYtd ?? 0;
  const tripsInvoiced = kpiData?.tripsInvoicedYtd ?? 0;

  const forecast = useMemo(
    () => computeTripForecast(tripsTraveled, goalTrips),
    [tripsTraveled, goalTrips],
  );

  const canEdit =
    goalScope === "personal" ||
    (goalScope === "team" && canEditTeamGoals);

  if (error) {
    return (
      <SectionLoadError
        sectionName="Trips Traveled"
        error={error}
        onRetry={() => {
          queryClient.invalidateQueries({ queryKey: ["/api/invoices/trips-traveled-kpis"] });
          queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="rounded-xl border bg-card p-5">
        <Skeleton className="h-4 w-36 mb-3" />
        <div className="flex gap-6 mb-3">
          <div>
            <Skeleton className="h-10 w-16 mb-1" />
            <Skeleton className="h-3 w-24" />
          </div>
          <div>
            <Skeleton className="h-10 w-16 mb-1" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
        <Skeleton className="h-6 w-full rounded-lg" />
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-card overflow-hidden flex flex-col">
      <div className="p-5 pb-4">
        <div className="flex items-center gap-1.5 mb-3">
          <p className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
            YTD Trip Metrics
          </p>
          <InfoHint label="How YTD trip metrics are calculated">
            <p>
              <strong>Trips Traveled</strong>: Trips with a return date that has
              occurred within the current year.
            </p>
            <p className="mt-1">
              <strong>Trips Invoiced</strong>: Trips with at least one invoice
              with a return date scheduled for this year.
            </p>
            <KpiBasisNote card="tripsTraveled" />
          </InfoHint>
          <KpiBasisLabel card="tripsTraveled" />
        </div>

        <div className="flex gap-6">
          <div className="min-w-0">
            <p className="text-3xl font-bold tracking-tight text-foreground">{tripsTraveled}</p>
            <p className="text-sm text-muted-foreground mt-0.5">Trips Traveled</p>
          </div>
          <div className="min-w-0 sm:border-l sm:pl-6">
            <p className="text-3xl font-bold tracking-tight text-foreground">{tripsInvoiced}</p>
            <p className="text-sm text-muted-foreground mt-0.5">Trips Invoiced</p>
          </div>
        </div>

        {goalTrips > 0 && (
          <p className="text-sm text-muted-foreground mt-1.5">
            /{goalTrips} goal
          </p>
        )}
      </div>

      {goalTrips > 0 ? (
        <div className="bg-muted/40 px-5 py-3.5 flex items-start justify-between gap-2 mt-auto border-t border-border/50">
          <div className="min-w-0 flex-1">
            <p
              className={`text-sm font-semibold ${
                forecast.onTrack
                  ? "text-green-700 dark:text-green-400"
                  : "text-red-600 dark:text-red-400"
              }`}
            >
              {forecast.onTrack ? "On Track" : "Off Track"}
            </p>
            <p className="text-sm text-muted-foreground break-words">
              {forecast.label}
            </p>
          </div>
          {canEdit && (
            <GoalEditDialog
              title={goalScope === "team" ? "Team Goals" : "Personal Goals"}
              description={`Set ${goalScope} targets for ${currentYear}.`}
              goal={activeGoal ?? null}
              year={currentYear}
              onSave={async (p) => {
                if (goalScope === "team") {
                  await upsertTeam.mutateAsync(p);
                } else {
                  await upsertPersonal.mutateAsync(p);
                }
              }}
              isSaving={upsertPersonal.isPending || upsertTeam.isPending}
            />
          )}
        </div>
      ) : (
        <div className="bg-muted/40 px-5 py-3.5 flex items-center justify-between gap-2 mt-auto border-t border-border/50">
          <p className="text-sm text-muted-foreground">
            No trips traveled goal set
          </p>
          {canEdit && (
            <GoalEditDialog
              title={goalScope === "team" ? "Team Goals" : "Personal Goals"}
              description={`Set ${goalScope} targets for ${currentYear}.`}
              goal={activeGoal ?? null}
              year={currentYear}
              onSave={async (p) => {
                if (goalScope === "team") {
                  await upsertTeam.mutateAsync(p);
                } else {
                  await upsertPersonal.mutateAsync(p);
                }
              }}
              isSaving={upsertPersonal.isPending || upsertTeam.isPending}
            />
          )}
        </div>
      )}
    </div>
  );
}
